/**
 * Routing prefixes — the dependency-free half of `@/lib/routing`.
 *
 * Types, defaults, env overrides and the sync shims live here so client
 * components and the Edge proxy can import them without pulling the server
 * fetch path (zod, React.cache, API tags) into their bundles. Server code keeps
 * importing from `@/lib/routing`, which re-exports everything below.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RoutingPrefixes = {
  packages: string; // e.g. "activities"
  destinations: string; // "" = absolute root slug (/[slug])
  blogs: string; // "" = absolute root slug (/[slug])
  articles: string; // "" = absolute root slug (/[slug])
  blog_category: string; // "" = absolute root slug (/[slug]); e.g. "blog-category"
  team: string; // e.g. "team"
  author: string; // e.g. "author"
  attraction: string; // e.g. "attraction" — places/landmarks, a sibling of destinations
  search: string; // e.g. "find"
  booking_lookup: string; // e.g. "booking/lookup"
};

export type RoutableKind = keyof RoutingPrefixes;

/**
 * Content kinds that can be served through a non-empty URL prefix segment via
 * the `(content)/[prefix]` routes. Kinds whose configured prefix is empty are
 * "absolute" — they live at the root (`/[slug]`) and are resolved by the
 * `[...slug]` catch-all + page-bundle instead of by `parsePrefix`.
 */
export type ContentKind =
  | "packages"
  | "destinations"
  | "blogs"
  | "articles"
  | "blog_category";

/** Kinds that render through the `(content)/[prefix]` route family. */
export type PrefixedKind = ContentKind | "team" | "author" | "attraction";

/**
 * Kinds that are resolved by matching a URL prefix segment. Absolute kinds
 * (empty configured prefix) are intentionally excluded — see parsePrefix.
 */
const PREFIXED_KINDS: PrefixedKind[] = [
  "packages",
  "destinations",
  "blogs",
  "articles",
  "blog_category",
  "team",
  "author",
  "attraction",
];

// ---------------------------------------------------------------------------
// Defaults (canonical fallback — matches the backend's own hardcoded defaults)
// ---------------------------------------------------------------------------

export const DEFAULT_ROUTING_PREFIXES: RoutingPrefixes = {
  packages: "activities",
  // Absolute (root) content — resolved by the [...slug] catch-all + page-bundle.
  destinations: "",
  blogs: "",
  articles: "",
  blog_category: "",
  team: "team",
  author: "author",
  attraction: "attraction",
  search: "search",
  booking_lookup: "booking/lookup",
};

// ---------------------------------------------------------------------------
// Env overrides (optional, per-deployment)
// ---------------------------------------------------------------------------

export function envOverrides(): Partial<RoutingPrefixes> {
  const o: Partial<RoutingPrefixes> = {};
  if (process.env.ROUTING_PACKAGES) o.packages = process.env.ROUTING_PACKAGES;
  if (process.env.ROUTING_DESTINATIONS)
    o.destinations = process.env.ROUTING_DESTINATIONS;
  if (process.env.ROUTING_BLOGS) o.blogs = process.env.ROUTING_BLOGS;
  if (process.env.ROUTING_ARTICLES) o.articles = process.env.ROUTING_ARTICLES;
  if (process.env.ROUTING_BLOG_CATEGORY)
    o.blog_category = process.env.ROUTING_BLOG_CATEGORY;
  if (process.env.ROUTING_TEAM) o.team = process.env.ROUTING_TEAM;
  if (process.env.ROUTING_AUTHOR) o.author = process.env.ROUTING_AUTHOR;
  if (process.env.ROUTING_ATTRACTION)
    o.attraction = process.env.ROUTING_ATTRACTION;
  if (process.env.ROUTING_SEARCH) o.search = process.env.ROUTING_SEARCH;
  if (process.env.ROUTING_BOOKING_LOOKUP)
    o.booking_lookup = process.env.ROUTING_BOOKING_LOOKUP;
  return o;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const normalizePrefix = (value: string): string =>
  value
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .replace(/\/{2,}/g, "/");

export function applyNormalization(prefixes: RoutingPrefixes): RoutingPrefixes {
  return {
    packages: normalizePrefix(prefixes.packages),
    destinations: normalizePrefix(prefixes.destinations),
    blogs: normalizePrefix(prefixes.blogs),
    articles: normalizePrefix(prefixes.articles),
    blog_category: normalizePrefix(prefixes.blog_category),
    team: normalizePrefix(prefixes.team),
    author: normalizePrefix(prefixes.author),
    attraction: normalizePrefix(prefixes.attraction),
    search: normalizePrefix(prefixes.search),
    booking_lookup: normalizePrefix(prefixes.booking_lookup),
  };
}

/**
 * Match a URL prefix segment to its routable kind. Absolute kinds (empty
 * configured prefix) never match here — a bare `/[slug]` is handled by the
 * `[...slug]` catch-all, not by `(content)/[prefix]`.
 */
export function matchPrefix(
  prefixes: RoutingPrefixes,
  segment: string,
): PrefixedKind | null {
  const normalized = normalizePrefix(segment);
  if (!normalized) return null;
  for (const kind of PREFIXED_KINDS) {
    const prefix = normalizePrefix(prefixes[kind]);
    if (prefix && prefix === normalized) return kind;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Sync compat shims — use ONLY in proxy.ts (Edge runtime, no React.cache)
// and in the static LEGACY_PREFIXES map. Everywhere else use the async forms.
// ---------------------------------------------------------------------------

/** Sync version of resolveRouting for use in proxy.ts (Edge runtime). */
export function resolveRoutingSync(): RoutingPrefixes {
  return applyNormalization({ ...DEFAULT_ROUTING_PREFIXES, ...envOverrides() });
}

/** Sync version of pathFor for use in proxy.ts (Edge runtime). */
export function pathForSync(kind: RoutableKind, slug?: string): string {
  const prefixes = resolveRoutingSync();
  const prefix = prefixes[kind];
  const path = slug ? `${prefix}/${slug.replace(/^\/+/, "")}` : prefix;
  return `/${normalizePrefix(path)}`;
}

/** Sync version of parsePrefix for use in proxy.ts (Edge runtime). */
export function parsePrefixSync(segment: string): PrefixedKind | null {
  return matchPrefix(resolveRoutingSync(), segment);
}
